import Seo from "@/components/Seo";
import React, { useEffect, useState, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Wrench, Briefcase, CalendarDays, Newspaper, ArrowRight, Sparkles, Flame, TrendingUp, MapPin, Wifi, Clock, Database } from "lucide-react";
import api from "@/lib/api";
import CrossSellBanner from "@/components/CrossSellBanner";
import CountryPanel from "@/components/globe/CountryPanel";
import GlobeLeaderboard from "@/components/globe/GlobeLeaderboard";
import NewsletterSignup from "@/components/NewsletterSignup";
import { useConfig } from "@/lib/useConfig";

const AIGlobe = lazy(() => import("@/components/globe/AIGlobe"));

const AVATAR_COLORS = ["#00F0FF", "#8B5CF6", "#22C55E", "#F59E0B", "#EF4444", "#3B82F6", "#14B8A6", "#EC4899"];
const AI_HUMAN_FIGURE = "https://static.prod-images.emergentagent.com/jobs/db9b0325-66fc-4389-ab58-770e17a502ae/images/672e7b038ded43798a613fbb21b60b33de64a27f14b7c50286d5660e8aa41f1f.png";


const fmtSalary = (j) => {
  if (j.salary_text) return j.salary_text;
  if (j.salary_min) {
    const sym = { USD: "$", EUR: "€", GBP: "£" }[j.salary_currency] || (j.salary_currency ? j.salary_currency + " " : "");
    const k = (n) => (n >= 1000 ? `${Math.round(n / 1000)}k` : n);
    return j.salary_max && j.salary_max !== j.salary_min ? `${sym}${k(j.salary_min)} - ${sym}${k(j.salary_max)}` : `${sym}${k(j.salary_min)}`;
  }
  return "Not disclosed";
};

const CAT_COLORS = {
  Models: "bg-[#8B5CF6]/15 text-[#a78bfa] border-[#8B5CF6]/30",
  Research: "bg-[#3B82F6]/15 text-[#60a5fa] border-[#3B82F6]/30",
  Industry: "bg-[#14B8A6]/15 text-[#2dd4bf] border-[#14B8A6]/30",
  Funding: "bg-[#22C55E]/15 text-[#4ade80] border-[#22C55E]/30",
  Regulation: "bg-[#F59E0B]/15 text-[#fbbf24] border-[#F59E0B]/30",
  Safety: "bg-[#EF4444]/15 text-[#f87171] border-[#EF4444]/30",
  Products: "bg-[#00F0FF]/15 text-[#00F0FF] border-[#00F0FF]/30",
};

const timeAgo = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const sections = [
  { to: "/tools", icon: Wrench, title: "AI Tools Marketplace", desc: "Discover and list the best AI tools with featured placement.", tag: "MARKETPLACE" },
  { to: "/jobs", icon: Briefcase, title: "Global AI Jobs Board", desc: "Hire and get hired anywhere in the world.", tag: "CAREERS" },
  { to: "/events", icon: CalendarDays, title: "AI Events Directory", desc: "Conferences, summits and hackathons worldwide.", tag: "EVENTS" },
  { to: "/news", icon: Newspaper, title: "AI News Feed", desc: "Daily AI-generated summaries of what matters.", tag: "NEWS" },
];

const CountUp = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!value) { setDisplay(0); return; }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{display}</>;
};

export default function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ tools: 0, jobs: 0, events: 0 });
  const [trending, setTrending] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [featuredTools, setFeaturedTools] = useState([]);
  const [whatsNew, setWhatsNew] = useState({ data_items: [], jobs: [], new_stats: 0, new_jobs: 0 });
  const [countrySel, setCountrySel] = useState(null);
  const { paymentsEnabled } = useConfig();

  useEffect(() => {
    Promise.all([api.get("/tools"), api.get("/jobs"), api.get("/events")])
      .then(([t, j, e]) => setStats({ tools: t.data.length, jobs: j.data.length, events: e.data.length }))
      .catch(() => {});
    api.get("/articles/trending", { params: { limit: 6 } })
      .then((r) => setTrending(r.data.articles || []))
      .catch(() => {});
    api.get("/live-jobs")
      .then((r) => {
        setJobs((r.data.jobs || []).slice(0, 5));
        setStats((s) => ({ ...s, jobs: r.data.total || s.jobs }));
      })
      .catch(() => {});
    api.get("/tools/featured", { params: { limit: 8 } })
      .then((r) => setFeaturedTools(r.data || []))
      .catch(() => {});
    api.get("/whats-new")
      .then((r) => setWhatsNew({ data_items: r.data.data_items || [], jobs: r.data.jobs || [], new_stats: r.data.new_stats || 0, new_jobs: r.data.new_jobs || 0 }))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="WorldAIInfo — AI Tools, Jobs, Events, Courses & News Hub"
        description="Explore the global AI hub — real, cited data on AI tools, jobs, events, courses, country rankings and model progress. Updated daily. Free to use."
        canonicalPath="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", "name": "WorldAIInfo", "url": "https://www.worldaiinfo.com", "logo": "https://www.worldaiinfo.com/icon-512.png", "sameAs": ["https://www.linkedin.com/company/worldaiinfo/"] },
            { "@type": "WebSite", "name": "WorldAIInfo", "url": "https://www.worldaiinfo.com",
              "potentialAction": { "@type": "SearchAction", "target": "https://www.worldaiinfo.com/tools?search={search_term_string}", "query-input": "required name=search_term_string" } }
          ]
        }}
      />
      {/* Hero */}
      <section className="relative overflow-hidden grid-bg">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0B0F19]/40 to-[#0B0F19]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 border border-white/10 rounded-full px-4 py-1.5 mb-6 bg-[#111827]">
              <Sparkles className="w-3.5 h-3.5 text-[#00F0FF]" />
              <span className="font-mono-tech text-xs text-[#94A3B8]">THE GLOBAL AI INDUSTRY HUB</span>
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              Everything happening in <span className="text-[#00F0FF] text-glow">Artificial Intelligence</span>, in one place.
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#94A3B8] max-w-2xl">
              Explore AI tools, discover global jobs, track events, and stay current with daily news — built for companies and professionals shaping the future.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/tools" data-testid="hero-explore-tools"
                className="bg-[#00F0FF] text-[#04121a] font-semibold px-6 py-3 rounded-md hover:brightness-110 transition flex items-center gap-2">
                Explore Tools <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/register" data-testid="hero-list-business"
                className="border border-white/15 px-6 py-3 rounded-md hover:border-[#00F0FF]/60 transition font-medium">
                List your business
              </Link>
            </div>
            <div className="mt-14 flex gap-10">
              {[["Tools", stats.tools], ["Jobs", stats.jobs], ["Events", stats.events]].map(([l, v]) => (
                <div key={l}>
                  <div className="font-display font-bold text-3xl text-[#00F0FF]" data-testid={`hero-stat-${l.toLowerCase()}`}><CountUp value={v} />+</div>
                  <div className="font-mono-tech text-xs uppercase text-[#94A3B8]">{l}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* 3D Rotating Earth Globe */}
          <div className="relative w-full h-[380px] sm:h-[460px] lg:h-[560px]" data-testid="hero-globe-wrap">
            <Suspense fallback={<div className="w-full h-full flex items-center justify-center text-[#00F0FF] font-mono-tech text-xs">Loading globe…</div>}>
              <AIGlobe onSelect={setCountrySel} paused={!!countrySel} />
            </Suspense>
          </div>
        </div>
      </section>

      <CountryPanel selection={countrySel} onClose={() => setCountrySel(null)} />

      {/* Top 10 AI nations leaderboard (same cited index as the globe) */}
      <GlobeLeaderboard onSelect={setCountrySel} />

      {/* Newsletter capture — slim bar just under the hero */}
      <NewsletterSignup variant="bar" source="home_top" testid="home-newsletter-bar" />

      {/* AI-in-your-job teaser */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-6">
        <Link to="/ai-in-your-job" data-testid="home-jobexposure-teaser"
          className="group block rounded-2xl border border-[#00F0FF]/25 bg-gradient-to-r from-[#0e1424] to-[#111a2e] p-6 md:p-8 hover:border-[#00F0FF]/50 transition">
          <div className="flex items-center justify-between gap-6 flex-wrap">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#00F0FF]" />
                <span className="font-mono-tech text-xs uppercase text-[#00F0FF] tracking-wider">Real data · 756 jobs</span>
              </div>
              <h3 className="font-display font-bold text-2xl md:text-3xl">How much is AI <span className="text-[#00F0FF]">already doing</span> in your job?</h3>
              <p className="mt-2 text-sm md:text-base text-[#94A3B8] max-w-2xl">
                Search your job title to see the share of its tasks where AI is used today — based on real usage data, not predictions.
              </p>
            </div>
            <span className="inline-flex items-center gap-2 bg-[#00F0FF] text-[#04121a] font-semibold px-5 py-3 rounded-md group-hover:brightness-110 transition shrink-0">
              Check your job <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>
      </section>

      {/* AI Human teaser */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-6">
        <Link to="/ai-human" data-testid="home-aihuman-teaser"
          className="group relative block overflow-hidden rounded-2xl border border-[#00F0FF]/25 bg-gradient-to-r from-[#0b1120] to-[#111a2e] hover:border-[#00F0FF]/50 transition">
          <div className="flex items-center gap-6 flex-wrap md:flex-nowrap p-6 md:p-8">
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#00F0FF]" />
                <span className="font-mono-tech text-xs uppercase text-[#00F0FF] tracking-wider">New · 65+ tools · 13 abilities</span>
              </div>
              <h3 className="font-display font-bold text-2xl md:text-3xl">
                The best AI tools for <span className="text-[#00F0FF]">every human ability</span>
              </h3>
              <p className="mt-2 text-sm md:text-base text-[#94A3B8] max-w-2xl">
                Meet <span className="text-white font-medium">AI Human</span> — tap any body part to explore top AI tools, each with verified, publicly-sourced usage data.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 bg-[#00F0FF] text-[#04121a] font-semibold px-5 py-3 rounded-md group-hover:brightness-110 transition">
                Explore AI Human <ArrowRight className="w-4 h-4" />
              </span>
            </div>
            <img src={AI_HUMAN_FIGURE} alt="AI Human holographic figure"
              className="h-40 md:h-52 w-auto object-contain shrink-0 mx-auto drop-shadow-[0_0_24px_rgba(0,240,255,0.35)] group-hover:scale-105 transition-transform duration-500" />
          </div>
        </Link>
      </section>


      {/* What's New */}
      {(whatsNew.data_items.length > 0 || whatsNew.jobs.length > 0) && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pt-8" data-testid="whats-new-section">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="inline-flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-[#00F0FF]" />
                <span className="font-mono-tech text-xs uppercase text-[#00F0FF] tracking-wider">What's New</span>
              </div>
              <h2 className="font-display font-bold text-2xl md:text-3xl">Latest on WorldAIInfo</h2>
            </div>
            {(whatsNew.new_stats > 0 || whatsNew.new_jobs > 0) && (
              <span className="inline-flex items-center gap-1.5 font-mono-tech text-xs uppercase px-3 py-1.5 rounded-full border border-[#22C55E]/30 text-[#4ade80] bg-[#22C55E]/5" data-testid="whats-new-count">
                <Sparkles className="w-3.5 h-3.5" />
                {[
                  whatsNew.new_stats > 0 ? `${whatsNew.new_stats} new stat${whatsNew.new_stats === 1 ? "" : "s"}` : null,
                  whatsNew.new_jobs > 0 ? `${whatsNew.new_jobs} new job${whatsNew.new_jobs === 1 ? "" : "s"}` : null,
                ].filter(Boolean).join(" · ")} this week
              </span>
            )}
          </div>

          {whatsNew.data_items.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-4 h-4 text-[#94A3B8]" />
                <h3 className="font-display font-semibold text-sm text-[#CBD5E1]">New data &amp; statistics</h3>
                <Link to="/ai-progress" className="ml-auto text-xs text-[#00F0FF] hover:underline" data-testid="whats-new-data-all">View all →</Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {whatsNew.data_items.map((it) => (
                  <Link key={it.id} to={`/data/${it.id}`} data-testid={`whats-new-data-${it.anchor}`}
                    className="group card-surface rounded-xl p-4 flex flex-col gap-2 hover:border-[#00F0FF]/40 transition-colors">
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono-tech uppercase text-[#00F0FF]">
                      <Clock className="w-3 h-3" /> Added {timeAgo(it.created_at)}
                    </span>
                    <span className="font-display font-semibold text-sm leading-snug group-hover:text-white line-clamp-2">{it.title}</span>
                    {it.source_name && <span className="text-xs text-[#64748B] mt-auto truncate">{it.source_name}</span>}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {whatsNew.jobs.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Briefcase className="w-4 h-4 text-[#94A3B8]" />
                <h3 className="font-display font-semibold text-sm text-[#CBD5E1]">Newest AI jobs</h3>
                <Link to="/jobs" className="ml-auto text-xs text-[#00F0FF] hover:underline" data-testid="whats-new-jobs-all">View all →</Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {whatsNew.jobs.map((j) => (
                  <Link key={j.id} to={`/jobs?company=${encodeURIComponent(j.source)}`} data-testid={`whats-new-job-${j.id}`}
                    className="group card-surface rounded-xl p-4 flex flex-col gap-2 hover:border-[#00F0FF]/40 transition-colors">
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono-tech uppercase text-[#4ade80]">
                      <Clock className="w-3 h-3" /> {timeAgo(j.published_at)}
                    </span>
                    <span className="font-display font-semibold text-sm leading-snug group-hover:text-white line-clamp-2">{j.title}</span>
                    <span className="text-xs text-[#64748B] mt-auto truncate">{j.company}{j.country ? ` · ${j.country}` : ""}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      )}


      {/* Trending this week */}
      {trending.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pt-6 pb-10" data-testid="trending-section">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-[#00F0FF]" />
                <span className="font-mono-tech text-xs uppercase text-[#00F0FF] tracking-wider">Trending this week</span>
              </div>
              <h2 className="font-display font-bold text-3xl md:text-4xl">The hottest AI stories right now</h2>
            </div>
            <Link to="/news" data-testid="trending-view-all"
              className="text-sm text-[#00F0FF] font-medium flex items-center gap-1.5 hover:gap-2.5 transition-all">
              View all news <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {trending.map((a, i) => (
              <motion.a key={a.id} href={a.article_url} target="_blank" rel="noopener noreferrer"
                data-testid={`trending-article-${i}`}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.06 }}
                className="card-surface rounded-xl p-6 block group hover:border-[#00F0FF]/40 transition-colors">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <span className={`font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded border ${CAT_COLORS[a.category] || CAT_COLORS.Industry}`}>
                    {a.category}
                  </span>
                  {a.is_hot && (
                    <span className="flex items-center gap-1 font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#EF4444]/20 text-[#f87171] border border-[#EF4444]/40">
                      <Flame className="w-3 h-3" /> Hot
                    </span>
                  )}
                </div>
                <h3 className="font-display font-semibold text-lg leading-snug mb-3 group-hover:text-[#00F0FF] transition-colors line-clamp-3">
                  {a.title}
                </h3>
                <div className="flex items-center gap-2 text-xs text-[#94A3B8] font-mono-tech">
                  <span>{a.source_name}</span>
                  <span>·</span>
                  <span>{timeAgo(a.published_at)}</span>
                </div>
              </motion.a>
            ))}
          </div>
        </section>
      )}

      {/* Featured Tools (paid placements) */}
      {featuredTools.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-10" data-testid="featured-tools-section">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#00F0FF]" />
                <span className="font-mono-tech text-xs uppercase text-[#00F0FF] tracking-wider">Featured Tools</span>
              </div>
              <h2 className="font-display font-bold text-3xl md:text-4xl">Spotlight on standout AI tools</h2>
            </div>
            <Link to="/tools" data-testid="featured-tools-view-all"
              className="text-sm text-[#00F0FF] font-medium flex items-center gap-1.5 hover:gap-2.5 transition-all">
              Browse all tools <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {featuredTools.map((t, i) => (
              <motion.div key={t.id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                onClick={() => navigate(`/tools/${t.id}`)} data-testid={`featured-tool-${i}`}
                className="card-surface rounded-xl p-5 cursor-pointer hover:border-[#00F0FF]/50 transition-colors flex flex-col">
                <div className="flex items-center gap-3 mb-3">
                  {t.logo_url
                    ? <img src={t.logo_url} alt={t.name} className="w-10 h-10 rounded-lg object-cover bg-white/5" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    : <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/15 text-[#00F0FF] flex items-center justify-center font-display font-bold">{t.name.charAt(0)}</div>}
                  <div className="min-w-0">
                    <h3 className="font-display font-semibold text-base truncate">{t.name}</h3>
                    <span className="font-mono-tech text-[10px] uppercase text-[#94A3B8]">{t.category}</span>
                  </div>
                </div>
                <p className="text-sm text-[#94A3B8] line-clamp-2 flex-1">{t.tagline || t.description}</p>
                <span className={`mt-3 self-start font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded-full border ${t.tier === "premium" ? "bg-[#8B5CF6]/15 text-[#a78bfa] border-[#8B5CF6]/30" : "bg-[#00F0FF]/10 text-[#00F0FF] border-[#00F0FF]/30"}`}>
                  {t.tier === "premium" ? "★ Premium" : "★ Featured"}
                </span>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Latest AI Jobs */}
      {jobs.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-10" data-testid="latest-jobs-section">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 mb-2">
                <Briefcase className="w-4 h-4 text-[#00F0FF]" />
                <span className="font-mono-tech text-xs uppercase text-[#00F0FF] tracking-wider">Now Hiring</span>
              </div>
              <h2 className="font-display font-bold text-3xl md:text-4xl">Latest AI jobs worldwide</h2>
            </div>
            <Link to="/jobs" data-testid="latest-jobs-view-all"
              className="text-sm text-[#00F0FF] font-medium flex items-center gap-1.5 hover:gap-2.5 transition-all">
              Browse all jobs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex flex-col gap-3">
            {jobs.map((j, i) => {
              const color = AVATAR_COLORS[(j.company || "?").charCodeAt(0) % AVATAR_COLORS.length];
              return (
                <motion.a key={j.id} href={j.apply_url} target="_blank" rel="noopener noreferrer"
                  data-testid={`home-job-${i}`}
                  initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="card-surface rounded-xl px-5 py-4 flex items-center gap-4 group hover:border-[#00F0FF]/40 transition-colors">
                  {j.company_logo
                    ? <img src={j.company_logo} alt={j.company} className="w-11 h-11 rounded-lg object-cover bg-white/5 shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    : <div className="w-11 h-11 rounded-lg flex items-center justify-center font-display font-bold shrink-0" style={{ backgroundColor: `${color}22`, color }}>{(j.company || "?").charAt(0).toUpperCase()}</div>}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-semibold text-base text-white truncate group-hover:text-[#00F0FF] transition-colors">{j.title}</h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-[#94A3B8] font-mono-tech">
                      <span>{j.company}</span>
                      <span className="flex items-center gap-1"><span className="text-sm">{j.country_flag}</span> {j.country}</span>
                      {j.is_remote && <span className="flex items-center gap-1 text-[#4ade80]"><Wifi className="w-3 h-3" /> Remote</span>}
                    </div>
                  </div>
                  <div className="hidden sm:block text-right shrink-0">
                    <div className="text-sm text-[#00F0FF] font-semibold">{fmtSalary(j)}</div>
                    <div className="text-xs text-[#64748B] font-mono-tech">{j.job_type}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#00F0FF] group-hover:translate-x-1 transition-all shrink-0" />
                </motion.a>
              );
            })}
          </div>
          <div className="mt-6 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="home-hiring-cta">
            <p className="text-sm text-[#fbbf24]"><span className="font-semibold">Hiring AI talent?</span> {paymentsEnabled ? "Post your job to our growing community — from $29 per listing." : "Post your job to our growing community — free while we're in pre-launch."}</p>
            <Link to="/jobs" data-testid="home-post-job-btn"
              className="bg-[#fbbf24] text-[#1a1204] font-semibold px-5 py-2.5 rounded-md hover:brightness-110 transition whitespace-nowrap">{paymentsEnabled ? "Post a Job" : "Post a Job — Free"}</Link>
          </div>
        </section>
      )}

      {/* Sections */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {sections.map((s, i) => (
            <motion.div key={s.to} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
              <Link to={s.to} data-testid={`home-section-${s.tag.toLowerCase()}`}
                className="card-surface rounded-xl p-8 block h-full group">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center">
                    <s.icon className="w-6 h-6 text-[#00F0FF]" />
                  </div>
                  <span className="font-mono-tech text-xs text-[#94A3B8] border border-white/10 px-2 py-1 rounded">{s.tag}</span>
                </div>
                <h3 className="font-display font-bold text-2xl mb-2 group-hover:text-[#00F0FF] transition-colors">{s.title}</h3>
                <p className="text-[#94A3B8]">{s.desc}</p>
                <div className="mt-6 flex items-center gap-2 text-sm text-[#00F0FF] font-medium">
                  Browse <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
      <CrossSellBanner />
    </div>
  );
}
